import { DriverClassification } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { startCase } from 'lodash-es';
import * as yup from 'yup';

import { EquipmentTypeItem } from '~hooks/useEquipment/models';
import { MaterialTypeahead } from '~hooks/useMaterials/models';
import UserStore from '~store/UserStore';
import { nullableStringOrNumberIsValidNumber } from '~utils/utilFunctions';

const grossCapacityFieldSchema = yup
  .number()
  .min(0.01, t('form_validation_errors.minimum_is_num', { num: 0.01 }))
  .required(t('form_validation_errors.required_simple'))
  .typeError(t('form_validation_errors.required_simple'));

const selectOptionSchema = yup.object().shape({
  label: yup.string().trim().required(),
  value: yup.string().trim().required(),
});

const additionalGrossCapacitySchema = yup.object().shape({
  capacity: grossCapacityFieldSchema.required(
    t('form_validation_errors.required_simple'),
  ),
  unitOfMeasure: selectOptionSchema.required(t('form_validation_errors.required_simple')),
});

function getDriverClassificationLabels() {
  return {
    [DriverClassification.FOUR_PLUS_AXLE_STRAIGHT]: t(
      'administration.equipment_type.driver_classifications.four_plus_axle_straight',
    ),
    [DriverClassification.THREE_AXLE]: t(
      'administration.equipment_type.driver_classifications.three_axle',
    ),
    [DriverClassification.TRACTOR_TRAILER]: t(
      'administration.equipment_type.driver_classifications.tractor_trailer',
    ),
    [DriverClassification.TWO_AXLE]: t(
      'administration.equipment_type.driver_classifications.two_axle',
    ),
  } satisfies Record<DriverClassification, string>;
}

function getDriverClassificationOption(driverClassification: DriverClassification) {
  return {
    label: getDriverClassificationLabels()[driverClassification],
    value: driverClassification,
    _entity: driverClassification,
  };
}

export function getDriverClassificationOptions() {
  const offeredClassifications = Object.keys(
    getDriverClassificationLabels(),
  ) as DriverClassification[];

  return offeredClassifications.map(getDriverClassificationOption);
}

export const equipmentTypeSchema = yup.object().shape({
  company: yup
    .object()
    .shape({
      label: yup.string().trim().required(),
      value: yup.string().trim().required(),
    })
    .required(t('form_validation_errors.required_simple')),
  name: yup
    .string()
    .trim()
    .min(1, t('form_validation_errors.required_simple'))
    .required(t('form_validation_errors.required_simple')),
  externalId: yup.string().trim().notRequired(),
  accountingId: yup.string().trim().notRequired(),
  grossCapacity: yup.lazy((val) => {
    return String(val).trim() === ''
      ? yup.string().notRequired()
      : grossCapacityFieldSchema;
  }),
  grossCapacityUnitOfMeasure: selectOptionSchema.notRequired(),
  driverClassification: yup
    .object()
    .shape({
      label: yup.string().trim().required(),
      value: yup.mixed<DriverClassification>().required(),
    })
    .nullable()
    .notRequired(),
  additionalGrossCapacities: yup.array(additionalGrossCapacitySchema).required(),
  materials: yup.array(selectOptionSchema).required(),
});

export type EquipmentTypeDto = yup.InferType<typeof equipmentTypeSchema>;
export type EquipmentTypeDtoAsyncError = {
  field: keyof EquipmentTypeDto;
  message: string;
};

export function setEquipmentTypeSchemaDefaultValues(
  userStore: UserStore,
  equipmentType?: EquipmentTypeItem,
) {
  const initialCompany = userStore.currentCompanies[0] || userStore.user.company;
  const initialGrossCapacityUnitOfMeasure = equipmentType?.grossCapacityUnitOfMeasure
    ? {
        label: equipmentType.grossCapacityUnitOfMeasure.name,
        value: equipmentType.grossCapacityUnitOfMeasure.id,
      }
    : null;
  const initialGrossCapacity = nullableStringOrNumberIsValidNumber(
    equipmentType?.grossCapacity ?? null,
  )
    ? Number(equipmentType?.grossCapacity)
    : '';
  const initialAdditionalGrossCapacities = equipmentType?.additionalGrossCapacities.length
    ? equipmentType.additionalGrossCapacities.map((grossCapacity) => {
        return {
          capacity: Number(grossCapacity.capacity),
          unitOfMeasure: {
            label: startCase(grossCapacity.unitOfMeasure.name.toLowerCase()),
            value: grossCapacity.unitOfMeasure.id,
          },
        };
      })
    : [];
  const initialMaterials = (equipmentType?.materials ?? []).map((material) => ({
    _entity: new MaterialTypeahead(
      material.id,
      material.name,
      material.externalId ?? null,
    ),
    label: material.name,
    value: material.id,
  }));

  const defaultValues: EquipmentTypeDto = {
    company: { label: initialCompany.legalName, value: initialCompany.id },
    name: equipmentType?.name ?? '',
    externalId: equipmentType?.externalId ?? '',
    accountingId: equipmentType?.accountingId ?? '',
    grossCapacity: initialGrossCapacity,
    grossCapacityUnitOfMeasure: initialGrossCapacityUnitOfMeasure,
    driverClassification: equipmentType?.driverClassification
      ? getDriverClassificationOption(equipmentType.driverClassification)
      : null,
    additionalGrossCapacities: initialAdditionalGrossCapacities,
    materials: initialMaterials,
  };

  return defaultValues;
}
